import { Injectable, computed, inject, signal } from "@angular/core";
import { catchError, finalize, map, Observable, of, shareReplay, tap } from "rxjs";
import { AuthService } from "@core/services/auth.service";
import { AuthState, AuthStatus, ChangePasswordRequest, ChangePasswordType, LoginRequest, MfaData, ResponseCode } from "@core/types";

@Injectable({ providedIn: "root" })
export class AuthStore {
  private readonly authService = inject(AuthService);

  private readonly isLoading = signal(false);
  private readonly _userState = signal<AuthState>({
    user: null,
    authStatus: AuthStatus.signed_out,
  });
  private readonly _loginAttempts = signal<number>(0);
  private readonly _mfaData = signal<MfaData | null>(null);

  readonly userState = computed(() => this._userState());
  readonly user = computed(() => this._userState().user);
  readonly userMfa = computed(() => this._mfaData());
  readonly isAuthenticated = computed(() => {
    const state = this._userState();
    return !!state.user && state.authStatus === AuthStatus.signed_in;
  });
  readonly loading = computed(() => this.isLoading());
  readonly loginAttempts = computed(() => this._loginAttempts());

  
  restoreSession(): Observable<void> {
    this.isLoading.set(true);

    return this.authService.getSession().pipe(
      tap((res) => {
        if (res.isSuccess && res.data) {
          this._userState.set({
            user: res.data,
            authStatus: res.data.mfaStatus === "valid" ? AuthStatus.signed_in : AuthStatus.mfa_pending,
          });
        } else {
          this.resetState();
        }
      }),
      map(() => void 0),
      finalize(() => this.isLoading.set(false)),
    );
  }

 
  login(payload: LoginRequest): Observable<ResponseCode> {
    this.isLoading.set(true);
    return this.authService.login(payload).pipe(
      tap((res) => {
        if (res.isSuccess) {
          const authUser = res.data?.authUser;
          this.patchState({
            user: authUser,
            authStatus: authUser?.mfaStatus === "valid" ? AuthStatus.signed_in : AuthStatus.mfa_pending,
          });
          if (res.code === ResponseCode.AUTH_MFA_REQUIRED) this._mfaData.set(res.data!.mfaInfo);
        } else {
          this._loginAttempts.update((v) => v + 1);
        }
      }),
      map((res) => res.code),
      finalize(() => this.isLoading.set(false)),
    );
  }

  
  logout(): Observable<void> {
    return this.authService.logout().pipe(
      tap(() => {
        this.resetState();
      }),
      map(() => void 0),
      catchError(() => of(void 0)),
    );
  }


   changePassword(payload: ChangePasswordRequest): Observable<ResponseCode> {
    this.isLoading.set(true);

    const request$ = payload.changeType === ChangePasswordType.Reset ? this.authService.resetPassword(payload) : this.authService.changePassword(payload);

    return request$.pipe(
      tap((res) => {
        if (res.code === ResponseCode.SUCCESS) this.resetState();
      }),
      map((res) => res.code),
      finalize(() => this.isLoading.set(false)),
    );
  }

  requestPasswordReset(username: string): Observable<ResponseCode> {
    this.isLoading.set(true);
    return this.authService.requestPasswordReset(username).pipe(
      map((res) => res.code),
      finalize(() => this.isLoading.set(false)),
    );
  }

  validatePasswordResetLink(username: string, token: string): Observable<ResponseCode> {
    this.isLoading.set(true);

    return this.authService.validateResetPasswordLink(username, token).pipe(
      map((res) => res.code),
      finalize(() => this.isLoading.set(false)),
    );
  }


  sendMfaCode(typeId: number): Observable<ResponseCode> {
    this.isLoading.set(true);

    return this.authService.sendMfaCode(typeId).pipe(
      map((res) => res.code),
      finalize(() => this.isLoading.set(false)),
    );
  }


  verifyMfaCode(code: string): Observable<ResponseCode> {
    this.isLoading.set(true);
    return this.authService.verifyMfaCode(code).pipe(
      map((res) => res.code),
      finalize(() => this.isLoading.set(false)),
    );
  }

  resetLoginAttemps() {
    this._loginAttempts.set(0);
  }

  /* =====================================================
   * Internal Helpers (never exposed)
   * ===================================================== */

  /** Partial, immutable state update */
  private patchState(patch: Partial<AuthState>): void {
    this._userState.update((state) => ({ ...state, ...patch }));
  }

  /** Hard reset to signed-out baseline */
  private resetState(): void {
    this._userState.set({
      user: null,
      authStatus: AuthStatus.signed_out,
    });
    this._loginAttempts.set(0);
  }
}
